import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Download, CheckCircle2, AlertTriangle, UserRoundPen, Repeat } from "lucide-react";
import AuditActions from "./AuditActions";
import { PUBLIC_ORIGIN } from "../../lib/domain";

const fmt = (n) => `${Number(n || 0).toFixed(2)} $`;

function Kpi({ label, value, tone = "", testId }) {
  return (
    <div className={`border p-3 ${tone || "border-white/10 bg-black/30"}`} data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-white/50">{label}</p>
      <p className="text-xl font-semibold text-white mt-1">{value}</p>
    </div>
  );
}

function Section({ title, count, children, testId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;
  return (
    <div className="border border-white/10 bg-black/20 mb-3" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5" data-testid={`${testId}-toggle`}>
        <span className="text-sm text-white/90 font-medium">{title}</span>
        <span className="text-xs text-white/60 border border-white/20 px-2 py-0.5">{count}</span>
      </button>
      {open && <div className="px-4 pb-4 overflow-x-auto">{children}</div>}
    </div>
  );
}

const Th = ({ children }) => <th className="text-left text-[11px] uppercase tracking-wider text-white/40 font-medium py-1.5 pr-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-1.5 pr-3 text-xs text-white/80 align-top ${className}`}>{children}</td>;

export const StripeFullAudit = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stripe-audit/full");
      setState(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit indisponible");
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/stripe-audit/auto-subscription/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (state?.status !== "running") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [state?.status, load]);

  const run = async () => {
    setBusy(true);
    try {
      await api.post("/stripe-audit/full/run");
      toast.info("Audit lancé — lecture complète de Stripe (30-90 s)…");
      setState((s) => ({ ...(s || {}), status: "running" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du lancement");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const T = state?.report?.totals || {};
    if (!window.confirm(`Appliquer les correspondances CERTAINES ?\n• ${T.subs_link_safe || 0} abonnement(s) à relier à un contrat existant\n• ${T.ps_safe || 0} dossier(s) Petits Samouraïs confirmés payés par Stripe\n• ${T.pkh_safe || 0} inscrite(s) PowerKick Her confirmées payées\n\nAucune création Stripe, aucun nouveau contrat.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/stripe-audit/full/apply?confirm=true", {}, { timeout: 120000 });
      toast.success(`${data.linked?.length || 0} liaison(s) · ${data.ps_done?.length || 0} PS · ${data.pkh_done?.length || 0} PKH — audit relancé`);
      setState((s) => ({ ...(s || {}), status: "running" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBusy(false);
    }
  };

  const applyNaming = async () => {
    const n = state?.report?.naming?.length || 0;
    if (!window.confirm(`Renommer ${n} client(s) Stripe : nom de l'ÉLÈVE, courriel du PARENT payeur, « Responsable : parent » en description.\nAucun impact financier.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/stripe-audit/full/apply-naming?confirm=true", {}, { timeout: 120000 });
      toast.success(`${data.done?.filter((d) => d.ok).length || 0}/${data.done?.length || 0} client(s) renommés`);
      setState((s) => ({ ...(s || {}), status: "running" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBusy(false);
    }
  };

  const createSub = async (row) => {
    const c = row.contracts?.[0];
    if (!c) return toast.error("Aucun contrat relié à ce paiement — crée/relie le contrat d'abord.");
    const base = row.why?.includes("+ taxes") ? Number((row.amount / 1.14975).toFixed(2)) : row.amount;
    if (!window.confirm(`Créer un abonnement Stripe pour ${c.nom} : ${fmt(base)} / 4 semaines (+ taxes), 1er prélèvement 28 jours après le paiement du ${row.date}.\nClient Stripe = nom de l'élève, courriel du parent, parent en Responsable.\n\nConfirmer ?`)) return;
    setCreating(row.charge_id);
    try {
      const { data } = await api.post("/stripe-audit/create-subscription", {
        contract_id: c.id, amount: base, paid_on: row.date, charge_id: row.charge_id, customer_id: row.customer_id, interval: "week", interval_count: 4,
      }, { timeout: 60000 });
      toast.success(`${data.action === "cree" ? "Abonnement créé" : data.action === "lie" ? "Abonnement existant relié" : data.action} ${data.subscription_id || ""}${data.card_on_file === false ? " — aucune carte : factures envoyées par courriel" : ""}`);
      setState((s) => ({ ...(s || {}), status: "running" }));
      run();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec Stripe");
    } finally {
      setCreating(null);
    }
  };

  const toggleAuto = async () => {
    try {
      const { data } = await api.put("/stripe-audit/auto-subscription/settings", { ...settings, enabled: !settings.enabled });
      setSettings(data);
      toast.success(data.enabled ? "Abonnement automatique ACTIVÉ" : "Abonnement automatique désactivé");
    } catch (e) {
      toast.error("Réglage non enregistré");
    }
  };

  const downloadMd = async () => {
    const { data } = await api.get("/stripe-audit/full/report.md", { responseType: "text" });
    const blob = new Blob([data], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit-stripe-${(state?.at || "").slice(0, 10)}.md`;
    a.click();
  };

  const R = state?.report;
  const T = R?.totals;
  const S = R?.stripe;
  const running = state?.status === "running";

  return (
    <div className="mt-10" data-testid="stripe-full-audit">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-base md:text-lg text-white font-semibold flex items-center gap-2"><ShieldCheck size={18} className="text-[#C9A961]" /> Audit Stripe LIVE complet</h2>
          <p className="text-xs text-white/50 mt-1 max-w-2xl">Clients, abonnements, paiements 12 mois, Petits Samouraïs, PowerKick Her et contrats élèves — croisés en lecture seule. Les écritures ne touchent que les correspondances certaines ; toute création d'abonnement exige ton clic.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={run} disabled={busy || running} data-testid="full-audit-run-btn"
            className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-50 text-white text-sm font-medium px-4 py-2 flex items-center gap-2">
            <RefreshCw size={16} className={running ? "animate-spin" : ""} /> {running ? "Audit en cours…" : "Lancer l'audit complet"}
          </button>
          {R && <button onClick={downloadMd} data-testid="full-audit-download-btn" className="border border-white/20 hover:bg-white/10 text-white/80 text-sm px-3 py-2 flex items-center gap-2"><Download size={15} /> Rapport .md</button>}
        </div>
      </div>

      {settings && (
        <div className="border border-white/10 bg-black/30 p-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="auto-sub-settings">
          <div className="text-xs text-white/70 flex items-center gap-2"><Repeat size={14} className="text-[#C9A961]" />
            <span><b>Facture payée → abonnement automatique</b> : client Stripe au nom de l'élève, courriel du parent, parent en « Responsable », 1er prélèvement après la période déjà payée, zéro doublon.</span>
          </div>
          <button onClick={toggleAuto} data-testid="auto-sub-toggle" className={`text-xs px-3 py-1.5 border ${settings.enabled ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>
            {settings.enabled ? "ACTIVÉ" : "DÉSACTIVÉ"}
          </button>
        </div>
      )}

      {state?.status === "error" && <div className="border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300 mb-4" data-testid="full-audit-error">Échec : {state.error}</div>}

      {R && (
        <>
          <p className="text-[11px] text-white/40 mb-2" data-testid="full-audit-meta">Dernier audit : {state.at?.replace("T", " ").slice(0, 16)} UTC · mode Stripe <b className="text-white/70">{R.stripe_mode?.toUpperCase()}</b> · {S.customers} clients · {S.live_subscriptions} abonnements vivants · {S.charges_12m} paiements sur 12 mois ({fmt(S.paid_12m)})</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Kpi label="MRR estimé" value={fmt(S.mrr_estime)} testId="kpi-mrr" />
            <Kpi label="Abonnements à relier (certain)" value={T.subs_link_safe} tone={T.subs_link_safe ? "border-emerald-500/40 bg-emerald-500/10" : ""} testId="kpi-link-safe" />
            <Kpi label="Abonnements dojo sans contrat" value={T.subs_without_contract_dojo} tone={T.subs_without_contract_dojo ? "border-amber-500/40 bg-amber-500/10" : ""} testId="kpi-no-contract" />
            <Kpi label="Contrats actifs sans abonnement" value={`${T.active_no_sub} · ≈ ${fmt(T.lost_monthly_estimate)}/mois`} tone={T.active_no_sub ? "border-red-500/40 bg-red-500/10" : ""} testId="kpi-active-no-sub" />
            <Kpi label="Paiements uniques → abonnement ?" value={T.onetime_should_sub} testId="kpi-onetime" />
            <Kpi label="Doublons Stripe" value={`${T.dup_customers} clients · ${T.dup_subs} abo.`} testId="kpi-dups" />
            <Kpi label="Petits Samouraïs à rapprocher" value={`${T.ps_to_reconcile} · ${T.ps_safe} confirmés`} testId="kpi-ps" />
            <Kpi label="Webhooks Stripe actifs" value={S.webhooks?.length || 0} tone={S.webhooks?.length ? "" : "border-red-500/40 bg-red-500/10"} testId="kpi-webhooks" />
          </div>

          <div className="flex gap-2 flex-wrap mb-5">
            <button onClick={apply} disabled={busy || running || !(T.subs_link_safe || T.ps_safe || T.pkh_safe)} data-testid="full-audit-apply-btn"
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 flex items-center gap-2">
              <CheckCircle2 size={16} /> Appliquer les correspondances certaines ({(T.subs_link_safe || 0) + (T.ps_safe || 0) + (T.pkh_safe || 0)})
            </button>
            <button onClick={applyNaming} disabled={busy || running || !T.naming} data-testid="full-audit-naming-btn"
              className="border border-[#C9A961]/50 text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-40 text-sm px-4 py-2 flex items-center gap-2">
              <UserRoundPen size={16} /> Renommer clients Stripe (élève + responsable) ({T.naming || 0})
            </button>
          </div>

          {!S.webhooks?.length && (
            <div className="border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200 mb-4 flex gap-2" data-testid="webhook-missing-alert">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span><b>Aucun webhook Stripe n'est enregistré sur ton compte.</b> Stripe ne prévient donc jamais la plateforme d'un paiement, d'un échec ou d'une annulation : c'est la cause principale des « rapprochement requis ». À créer dans Stripe › Développeurs › Webhooks : <code className="bg-black/40 px-1">{PUBLIC_ORIGIN}/api/stripe/lifecycle</code> (événements : checkout.session.completed, invoice.paid, invoice.payment_failed, customer.subscription.*, charge.refunded) puis coller la clé de signature dans <code className="bg-black/40 px-1">STRIPE_WEBHOOK_SECRET</code>.</span>
            </div>
          )}

          <Section title="Abonnements à relier à un contrat existant (certain)" count={T.subs_link_safe} testId="sec-link-safe" defaultOpen>
            <table className="w-full"><thead><tr><Th>Élève (contrat)</Th><Th>Client Stripe</Th><Th>Abonnement</Th><Th>Montant</Th><Th>Prochain</Th></tr></thead>
              <tbody>{R.subs_link_safe.map((x) => <tr key={x.subscription_id} className="border-t border-white/5"><Td>{x.contract_name || <i className="text-amber-300">contrat sans nom</i>}</Td><Td>{x.stripe_name}<br /><span className="text-white/40">{x.stripe_email}</span></Td><Td className="font-mono">{x.subscription_id}</Td><Td>{fmt(x.amount)} / {x.interval}</Td><Td>{x.next_due}</Td></tr>)}</tbody></table>
          </Section>

          <Section title="Abonnements Stripe SANS contrat élève" count={T.subs_without_contract} testId="sec-no-contract" defaultOpen>
            <table className="w-full"><thead><tr><Th>Client Stripe</Th><Th>Type</Th><Th>Abonnement</Th><Th>Montant</Th><Th>Depuis</Th></tr></thead>
              <tbody>{R.subs_without_contract.map((x) => <tr key={x.subscription_id} className="border-t border-white/5"><Td>{x.stripe_name}<br /><span className="text-white/40">{x.stripe_email}</span></Td><Td>{x.kind === "academie_en_ligne" ? <span className="text-sky-300">Académie en ligne (normal)</span> : x.kind === "petits_samourais" ? <span className="text-violet-300">Petits Samouraïs</span> : x.kind}</Td><Td className="font-mono">{x.subscription_id}</Td><Td>{fmt(x.amount)} / {x.interval}</Td><Td>{x.created}</Td></tr>)}</tbody></table>
            <p className="text-[11px] text-white/40 mt-2">Dojo / Petits Samouraïs : créer le contrat élève depuis « Contrats Élèves » puis « Lier Stripe » — jamais de doublon : vérifier d'abord que l'élève n'existe pas sous un autre courriel.</p>
          </Section>

          <Section title="Contrats actifs SANS abonnement vivant (revenu non prélevé)" count={T.active_no_sub} testId="sec-active-no-sub">
            <table className="w-full"><thead><tr><Th>Élève</Th><Th>Courriel</Th><Th>Discipline</Th><Th>Tarif</Th><Th>Dernier paiement</Th><Th>Source</Th></tr></thead>
              <tbody>{R.active_no_sub.map((x) => <tr key={x.contract_id} className="border-t border-white/5"><Td>{x.nom}{x.exempt && <span className="ml-1 text-emerald-300">(exempt)</span>}</Td><Td>{x.courriel}</Td><Td>{x.discipline}</Td><Td>{fmt(x.tarif_mensuel)}</Td><Td>{x.dernier_paiement || "—"}</Td><Td>{x.source || "—"}</Td></tr>)}</tbody></table>
          </Section>

          <Section title="Paiements uniques qui devraient être un abonnement" count={T.onetime_should_sub} testId="sec-onetime" defaultOpen>
            <table className="w-full"><thead><tr><Th>Date</Th><Th>Payeur</Th><Th>Montant</Th><Th>Pourquoi</Th><Th>Contrat</Th><Th>Action</Th></tr></thead>
              <tbody>{R.onetime_should_sub.map((x) => <tr key={x.charge_id} className="border-t border-white/5"><Td>{x.date}</Td><Td>{x.name}<br /><span className="text-white/40">{x.email}</span></Td><Td>{fmt(x.amount)}</Td><Td>{x.why}</Td><Td>{x.contracts?.map((c) => c.nom).join(", ") || <i className="text-amber-300">aucun</i>}</Td>
                <Td><button onClick={() => createSub(x)} disabled={!!creating || !x.contracts?.length} data-testid={`create-sub-${x.charge_id}`} className="border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 px-2 py-1 text-[11px]">{creating === x.charge_id ? "…" : "Créer l'abonnement"}</button></Td></tr>)}</tbody></table>
          </Section>

          <Section title="Contrats pointant vers un abonnement annulé / introuvable" count={T.contracts_dead_sub} testId="sec-dead-sub">
            <table className="w-full"><thead><tr><Th>Élève</Th><Th>Abonnement</Th><Th>État Stripe</Th><Th>Autre abonnement vivant</Th></tr></thead>
              <tbody>{R.contracts_dead_sub.map((x) => <tr key={x.contract_id} className="border-t border-white/5"><Td>{x.nom}</Td><Td className="font-mono">{x.subscription_id}</Td><Td className="text-amber-300">{x.stripe_status}</Td><Td className="font-mono">{x.alternative_live_subs?.join(", ") || "—"}</Td></tr>)}</tbody></table>
          </Section>

          <Section title="Abonnements ambigus (plusieurs contrats possibles)" count={T.subs_ambiguous} testId="sec-ambiguous">
            {R.subs_ambiguous.map((x) => <p key={x.subscription_id} className="text-xs text-white/80 py-1 border-t border-white/5">{x.stripe_name} · {x.stripe_email} · <span className="font-mono">{x.subscription_id}</span> → {x.contracts.map((c) => c.nom).join(" / ")}</p>)}
          </Section>

          <Section title="Clients Stripe en double (même courriel)" count={T.dup_customers} testId="sec-dup-customers">
            {R.dup_customers.map((x) => <p key={x.email} className="text-xs text-white/80 py-1 border-t border-white/5">{x.email} → {x.customers.map((c) => `${c.name || "?"} (${c.live_subs} abo.)`).join(" · ")}</p>)}
            <p className="text-[11px] text-white/40 mt-2">Normal pour une famille (1 client par enfant, même courriel parent). Fusionner seulement les doublons de la MÊME personne via Audit abonnements › Clients en double.</p>
          </Section>

          <Section title="Clients avec 2+ abonnements vivants" count={T.dup_subs} testId="sec-dup-subs">
            {R.dup_subs.map((x) => <p key={x.customer_id} className="text-xs text-white/80 py-1 border-t border-white/5">{x.name} · {x.email} → {x.subs.map((s) => `${s.id} ${fmt(s.amount)}/${s.interval} (${s.contract || "sans contrat"})`).join(" · ")}</p>)}
          </Section>

          <Section title="Petits Samouraïs — dossiers non payés vs Stripe" count={T.ps_to_reconcile} testId="sec-ps" defaultOpen>
            <table className="w-full"><thead><tr><Th>Enfant</Th><Th>Parent</Th><Th>Formule</Th><Th>Montant</Th><Th>Stripe</Th></tr></thead>
              <tbody>{R.ps.filter((x) => x.payment_status !== "paye").map((x) => <tr key={x.eid} className="border-t border-white/5"><Td>{x.child}</Td><Td>{x.parent}<br /><span className="text-white/40">{x.parent_email}</span></Td><Td>{x.formule}{!x.formule_officielle && <span className="text-amber-300"> (ancienne)</span>}</Td><Td>{fmt(x.amount)}</Td>
                <Td>{x.exact_match ? <span className="text-emerald-300">✅ {fmt(x.exact_match.amount)} le {x.exact_match.date}</span> : x.live_subscriptions?.length ? <span className="text-emerald-300">✅ abonnement actif {fmt(x.live_subscriptions[0].amount)}/{x.live_subscriptions[0].interval}</span> : x.stripe_matches?.length ? <span className="text-amber-300">{x.stripe_matches.length} paiement(s) d'un autre montant</span> : <span className="text-white/40">aucun paiement</span>}</Td></tr>)}</tbody></table>
          </Section>

          <Section title="PowerKick Her — inscrites vs paiements Stripe" count={(R.pkh?.length || 0) + (R.pkh_payments?.length || 0)} testId="sec-pkh">
            <table className="w-full"><thead><tr><Th>Date</Th><Th>Nom</Th><Th>Montant</Th><Th>Contrat</Th></tr></thead>
              <tbody>{R.pkh_payments.map((x) => <tr key={x.charge_id} className="border-t border-white/5"><Td>{x.date}</Td><Td>{x.name}<br /><span className="text-white/40">{x.email}</span></Td><Td>{fmt(x.amount)}</Td><Td>{x.contracts?.map((c) => c.nom).join(", ") || <i className="text-amber-300">aucun</i>}</Td></tr>)}</tbody></table>
          </Section>

          <Section title="Clients Stripe à renommer (nom de l'élève + responsable)" count={T.naming} testId="sec-naming">
            <table className="w-full"><thead><tr><Th>Élève</Th><Th>Nom Stripe actuel</Th><Th>Courriel</Th><Th>Responsable</Th></tr></thead>
              <tbody>{R.naming.map((x) => <tr key={x.contract_id} className="border-t border-white/5"><Td>{x.eleve}</Td><Td>{x.stripe_name || "—"}</Td><Td>{x.stripe_email}</Td><Td>{x.responsable || "—"}</Td></tr>)}</tbody></table>
          </Section>

          <Section title="Contrats actifs sans fiche membre (espace membre)" count={T.contracts_no_member} testId="sec-no-member">
            <p className="text-[11px] text-white/50 mb-2">L'espace membre se crée au 1er accès par courriel (élève ou parent) — ces contrats n'ont encore jamais ouvert leur espace.</p>
            <div className="flex flex-wrap gap-1">{R.contracts_no_member.map((x) => <span key={x.contract_id} className="text-[11px] text-white/70 border border-white/10 px-2 py-0.5">{x.nom}</span>)}</div>
          </Section>

          <AuditActions onChanged={run} />
        </>
      )}

      {!R && state?.status === "none" && <p className="text-xs text-white/40" data-testid="full-audit-empty">Aucun audit encore lancé.</p>}
    </div>
  );
};
